import { twJoin } from 'tailwind-merge';

import { GlassSurface, useLiquidGlass } from '@/components/ui/glass';

/** How dark the dim behind an opaque sheet is. */
const BACKDROP_OPACITY = 0.5;

/** Glass only refracts what is behind it, so the dim under a glass sheet is lighter. */
const GLASS_BACKDROP_OPACITY = 0.25;

const GLASS_SHEET_RADIUS_CLASS = 'rounded-[32px]';

type GlassSheet = {
  hasGlass: boolean;
  /** Merge last into the sheet's full-screen root. */
  rootClassName: string | false;
  /** Merge last into the sheet's content, so it wins over a call site's own shape. */
  contentClassName: string | false;
  backdropOpacity: number;
};

/**
 * What changes when a bottom sheet is drawn as Liquid Glass, for the two sheet
 * primitives to share: the app's sheet and the copy inside the select. They
 * are separate Gluestack instances, and both must change together or a picker
 * looks like a different app from the sheet beside it.
 *
 * On iOS 26 a sheet floats off the screen edges with all four corners rounded.
 * The float is padding on the root, not a margin on the content: call sites
 * size the content `w-full`, and a margin there would push it off the right
 * edge. The content drops its opaque fill, border and shadow, because the
 * glass draws its own, and clips so a square-cornered scroll view stays inside
 * the rounded shape. Everywhere else every value here is the sheet as it was.
 */
export function useGlassSheet(): GlassSheet {
  const hasGlass = useLiquidGlass();

  return {
    hasGlass,
    rootClassName: hasGlass && 'px-2 pb-2',
    contentClassName:
      hasGlass &&
      twJoin(GLASS_SHEET_RADIUS_CLASS, 'overflow-hidden border-0 bg-transparent shadow-none'),
    backdropOpacity: hasGlass ? GLASS_BACKDROP_OPACITY : BACKDROP_OPACITY,
  };
}

/**
 * The glass itself, as the first child of a sheet's content. It is a layer
 * behind the children rather than the content view: Gluestack animates and
 * measures the content view, and the glass must not take touches meant for the
 * sheet's own controls.
 */
export function GlassSheetBackground() {
  return (
    <GlassSurface
      pointerEvents="none"
      className={twJoin('absolute inset-0', GLASS_SHEET_RADIUS_CLASS)}
    />
  );
}
